import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import {
  loginUserAction,
  recoverPasswordAction,
  setLoginAnimation,
  loginWithGoogle,
} from "../../store/actions";
import { Eye, EyeClosed } from "lucide-react";
import styles from "./LoginNew.module.scss";
import LogoEdify from "../../assets/images/Logo-edify-no-bg.png";
import { fireAnalytics } from "../../utils/firebase.js";
import { useGoogleLogin, TokenResponse } from "@react-oauth/google";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import { hasStoredAuthenticatedSession } from "../../utils/session/hasStoredAuthenticatedSession.ts";
import { isGuestSession } from "../../utils/session/sessionGuest.ts";

interface LoginNewProps {
  error: boolean;
  success: boolean;
  loginAnimation: boolean;
  userData: any;
  loginUserAction: (values: any, navigate: any) => void;
  recoverPasswordAction: (values: any) => void;
  setLoginAnimation: (value: boolean) => void;
  loginWithGoogle: (credentials: any, navigate: any) => void;
}

interface LoginFormValues {
  email: string;
  password: string;
}

const LoginNew: React.FC<LoginNewProps> = (props) => {
  const [showHidePassword, setShowHidePassword] = useState(true);
  const [formData, setFormData] = useState<LoginFormValues>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Partial<LoginFormValues>>({});
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  // Hook de Google Login
  const login = useGoogleLogin({
    onSuccess: (tokenResponse: TokenResponse) => {
      fireAnalytics("login_google_iniciado", { 
        category: "authentication",
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        action: "google_login_initiated",
        login_method: "google"
      });
      if (tokenResponse.access_token) {
        props.loginWithGoogle(tokenResponse.access_token, navigate);
      }
    },
    onError: () => {
      fireAnalytics("login_google_error", { category: "authentication" });
    },
    scope: 'openid email profile',
  });

  const handleGoogleClick = () => {
    login();
  };

  useEffect(() => {
    fireAnalytics("login_landing", { 
      edify_user_id: props.userData?.id || '',
      category: "authentication",
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      action: "landing"
    });
    if (!localStorage.getItem("access-Token-Edify")) {
      return;
    }
    if (props.userData?.username === "invited@edify.la" || isGuestSession()) {
      fireAnalytics("login_invited", { category: "authentication" });
      navigate("/dashboard", { replace: true });
      return;
    }
    if (hasStoredAuthenticatedSession()) {
      navigate("/dashboard", { replace: true });
    }
  }, [props.userData, navigate]);


  const validateForm = (): boolean => {
    const newErrors: Partial<LoginFormValues> = {};

    if (!formData.email) {
      newErrors.email = "Escribe tu e-mail";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Debes ingresar un formato de email válido";
    }

    if (!formData.password) {
      newErrors.password = "Escribe tu contraseña";
    } else if (formData.password.length < 6) {
      newErrors.password = "La contraseña debe tener al menos 6 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof LoginFormValues, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      props.loginUserAction(formData, navigate);
      fireAnalytics("login_iniciated", { 
        category: "authentication",
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        action: "login_initiated",
        login_method: "email"
      });
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowHidePassword(!showHidePassword);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <motion.img
            src={LogoEdify}
            alt="Edify Logo"
            className={styles.logo}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          />
          <motion.h1 
            className={styles.mainTitle}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            ¡Bienvenido a Edify!
          </motion.h1>
          <motion.p 
            className={styles.mainSubtitle}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Queremos cambiar la manera de construir a partir de tecnología, creá tu cuenta gratis.
          </motion.p>
        </div>

        <div className={styles.formContainer}>
          <motion.form 
            className={`${styles.form} ${styles.animationContainer}`}
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="email">E-mail</label>
              <input
                type="email"
                placeholder="tu@email.com"
                className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit(e as any);
                  }
                }}
              />
              {errors.email && <span className={styles.errorText}>{errors.email}</span>}
            </div>

            <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="password">Contraseña</label>
              <div className={styles.passwordWrapper}>
                <input
                  type={showHidePassword ? "password" : "text"}
                  placeholder="********"
                  className={`${styles.input} ${styles.passwordInput} ${errors.password ? styles.inputError : ''}`}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSubmit(e as any);
                    }
                  }}
                />
                {formData.password && (
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={togglePasswordVisibility}
                  >
                    {showHidePassword ? <EyeClosed size={16} /> : <Eye size={16} />}
                  </button>
                )}
              </div>
              {errors.password && <span className={styles.errorText}>{errors.password}</span>}
            </div>

            <Link
              className={styles.forgotPassword}
              to="/forgotpassword"
              onClick={() =>
                fireAnalytics("navigate_to_forgot_password", {
                  category: "authentication",
                })
              }
            >
              <span className={styles.forgotPasswordText}>
                Olvidé la contraseña
              </span>
            </Link>

            <button
              type="submit"
              disabled={isLoading || !formData.email || !formData.password}
              className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
            >
              {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </button>
          </motion.form>

          {/* Divider y botón de Google personalizado */}
          <motion.div 
            className={styles.googleDivider}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <span className={styles.googleDivider_line}></span>
            <div className={styles.googleDivider_text}>O continúa con</div>
            <span className={styles.googleDivider_line}></span>
          </motion.div>

          <motion.button
            className={`${styles.googleButton} ${styles.animationContainer}`}
            onClick={handleGoogleClick}
            disabled={isLoading}
            type="button"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            <div className={styles.googleButton_content}>
              <svg 
                className={styles.googleButton_icon} 
                viewBox="0 0 24 24" 
                width="24" 
                height="24"
              >
                <path 
                  fill="#234ba2" 
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path 
                  fill="#2d5bc0" 
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path 
                  fill="#eef3ff" 
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path 
                  fill="#1e3d8a" 
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className={styles.googleButton_text}>
                Continuar con Google
              </span>
            </div>
          </motion.button>
        </div>

        <motion.div 
          className={styles.footer}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
        >
          <p className={styles.footerText}>
            ¿Todavía no tenés cuenta?{" "}
            <Link to="/register" className={styles.footerLink}>
              Regístrate
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    error: state.general.error,
    success: state.general.success,
    loginAnimation: state.general.loginAnimation,
    userData: state.general.userData,
  };
};

export default connect(mapStateToProps, {
  loginUserAction,
  recoverPasswordAction,
  setLoginAnimation,
  loginWithGoogle,
})(LoginNew);
